import { NavFooter } from '@/components/nav-footer';
import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { home as dashboard } from '@/routes/dashboard';
import { index as myBanners } from '@/routes/dashboard/banners';
import { index as myCategories } from '@/routes/dashboard/categories';
import { index as myClassifieds } from '@/routes/dashboard/classifieds';
import { index as myContactInquiries } from '@/routes/dashboard/contact-inquiries';
import { index as myFavorites } from '@/routes/dashboard/favorites';
import { index as myListingCategories } from '@/routes/dashboard/listing-categories';
import { index as myListings } from '@/routes/dashboard/listings';
import { index as myNewsletter } from '@/routes/dashboard/newsletter';
import { index as myPhoneVerifications } from '@/routes/dashboard/phone-verifications';
import { edit as profileEdit } from '@/routes/profile';
import { type NavItem } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import { FolderTree, Heart, Image, LayoutGrid, List, Mail, MessageSquare, Newspaper, Phone, Settings, Wrench } from 'lucide-react';
import AppLogo from './app-logo';

const allNavItems: NavItem[] = [
    {
        title: 'Dashboard',
        href: dashboard(),
        icon: LayoutGrid,
        roles: ['admin', 'basic'], // Admins and basic users can access, sponsors cannot
    },
    {
        title: 'Empresas',
        href: myListings(),
        icon: List,
        roles: ['admin', 'sponsor'], // Only admins and sponsors
    },
    {
        title: 'Categorias de Empresas',
        href: myListingCategories(),
        icon: FolderTree,
        roles: ['admin'], // Only admins
    },
    {
        title: 'Embarcações',
        href: myClassifieds(),
        icon: Newspaper,
        roles: ['admin', 'sponsor'], // Only admins and sponsors
    },
    {
        title: 'Categorias de Embarcações',
        href: myCategories(),
        icon: FolderTree,
        roles: ['admin'], // Only admins
    },
    {
        title: 'Opcionais e Equipamentos',
        href: '/dashboard/optionals',
        icon: Wrench,
        roles: ['admin'], // Only admins
    },
    {
        title: 'Banners',
        href: myBanners(),
        icon: Image,
        roles: ['admin'], // Only admins
    },
    {
        title: 'Newsletter',
        href: myNewsletter(),
        icon: Mail,
        roles: ['admin'], // Only admins
    },
    {
        title: 'Contatos',
        href: myContactInquiries(),
        icon: MessageSquare,
        roles: ['admin', 'sponsor'], // Admins and sponsors (sponsors see their own contacts)
    },
    {
        title: 'Verificações de Telefone',
        href: myPhoneVerifications(),
        icon: Phone,
        roles: ['admin', 'sponsor'], // Admins and sponsors (sponsors see their own verifications)
    },
    {
        title: 'Favoritos',
        href: myFavorites(),
        icon: Heart,
        roles: ['admin', 'sponsor', 'basic'], // All roles can access
    },
];

const footerNavItems: NavItem[] = [];

export function AppSidebar({ hideOnLarge = false }: { hideOnLarge?: boolean }) {
    const page = usePage();
    const user = page.props.auth?.user;
    const userRole = user?.role || 'basic';

    // Filter nav items based on user role
    const mainNavItems = allNavItems.filter((item) => {
        if (!item.roles) {
            return true; // If no roles specified, show to everyone
        }
        return item.roles.includes(userRole);
    });

    // Show settings only to admins
    const showSettings = userRole === 'admin';

    return (
        <Sidebar hideOnLarge={hideOnLarge} collapsible="icon" variant="inset">
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" asChild>
                            <Link href={dashboard()} prefetch>
                                <AppLogo />
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>

            <SidebarContent>
                <NavMain items={mainNavItems} />
                {showSettings && (
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild tooltip={{ children: 'Configurações' }}>
                                <Link href={profileEdit()} prefetch>
                                    <Settings />
                                    <span>Configurações</span>
                                </Link>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                )}
            </SidebarContent>

            <SidebarFooter>
                <NavFooter items={footerNavItems} className="mt-auto" />
                <NavUser />
            </SidebarFooter>
        </Sidebar>
    );
}
